import './styles.scss'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Galleria } from 'primereact/galleria'
import { Skeleton } from 'primereact/skeleton'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { NoData } from 'Assets/Images'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { fetchCommunityUnreadCount, fetchPersonalChatUnreadCount } from 'Services/Supabase'
import { subscribeToPersonalChatRealtime, subscribeToCommunityChatRealtime } from 'Services/chatRealtimeSubscriptions'
import CreatePostModal from './Components/CreatePostModal'
import CoffeeCommunities from '../CoffeeCommunities'
import PersonalChat from '../PersonalChat'

const CoffeePostScreen = (props) => {
  const { showModal, setShowModal, searchTextField, containerRef, hasHeader, onOrganizationStatusChange } = props
  const { t } = useTranslation()
  const pageSize = 10
  const MAX_DESCRIPTION_LENGTH = 120
  const userDetails = getLocalUserDetails()

  const [activeIndexes, setActiveIndexes] = useState({})
  const [mediaList, setMediaList] = useState([])
  const [posts, setPosts] = useState([])
  const [selectedCommunityId, setSelectedCommunityId] = useState(null)
  const [organizationStatus, setOrganizationStatus] = useState(null)
  const [loadingPosts, setLoadingPosts] = useState(true)
  const [loadingMorePosts, setLoadingMorePosts] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [pageNumber, setPageNumber] = useState(1)
  const [expandedPosts, setExpandedPosts] = useState(new Set())
  const [activeTab, setActiveTab] = useState('communityChat')
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [cameFromCommunityChat, setCameFromCommunityChat] = useState(false)
  const [personalUnreadCount, setPersonalUnreadCount] = useState(0)
  const [communityUnreadCount, setCommunityUnreadCount] = useState(0)

  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    fetchAllMediaPosts(true)
  }, [])

  // Handle URL params for chatTab and userId
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const chatTab = params.get('chatTab')
    const userId = params.get('userId')

    if (chatTab === 'personalChat') {
      if (userDetails?.id) {
        setActiveTab('personalChat')
        if (userId) {
          setSelectedUserId(userId)
        } else {
          setSelectedUserId(null)
        }
        setCameFromCommunityChat(false)
      } else {
        setActiveTab('personalChat')
        setSelectedUserId(null)
        setCameFromCommunityChat(false)
        params.set('chatTab', 'personalChat')
        params.delete('userId')
        history.replace({ pathname: location.pathname, search: params.toString() })
      }
    } else if (chatTab === 'communityChat') {
      setActiveTab('communityChat')
      setSelectedUserId(null)
      setCameFromCommunityChat(false)
    } else {
      // Default to communityChat if no chatTab is specified
      setActiveTab('communityChat')
      setSelectedUserId(null)
      setCameFromCommunityChat(false)
      // Update URL to include default chatTab
      params.set('chatTab', 'communityChat')
      params.delete('userId')
      history.replace({ pathname: location.pathname, search: params.toString() })
    }
  }, [location.search, history, location.pathname, userDetails?.id])

  const fetchUnreadCounts = useCallback(async () => {
    if (!userDetails?.id) {
      setPersonalUnreadCount(0)
      setCommunityUnreadCount(0)

      return
    }

    try {
      const [personalChatCount, communityCount] = await Promise.all([fetchPersonalChatUnreadCount(userDetails), fetchCommunityUnreadCount(userDetails)])

      setPersonalUnreadCount(personalChatCount)
      setCommunityUnreadCount(communityCount)
    } catch {
      setPersonalUnreadCount(0)
      setCommunityUnreadCount(0)
    }
  }, [userDetails?.id])

  const fetchUnreadCountsRef = useRef(fetchUnreadCounts)

  fetchUnreadCountsRef.current = fetchUnreadCounts

  useEffect(() => {
    if (!userDetails?.id) {
      setPersonalUnreadCount(0)
      setCommunityUnreadCount(0)

      return
    }

    fetchUnreadCounts()

    const personalCleanup = subscribeToPersonalChatRealtime({
      channelName: 'coffee-post-personal-chat-realtime',
      userId: userDetails?.id,
      onMessage: () => {
        fetchUnreadCountsRef.current?.()
      }
    })

    const communityCleanup = subscribeToCommunityChatRealtime({
      channelName: 'coffee-post-community-chat-realtime',
      userId: userDetails?.id,
      onMessage: () => {
        fetchUnreadCountsRef.current?.()
      }
    })

    return () => {
      personalCleanup()
      communityCleanup()
    }
  }, [userDetails?.id])

  const fetchAllMediaPosts = async (isInitialLoad = false) => {
    const userId = userDetails?.id || 0

    if (isInitialLoad) {
      setLoadingPosts(true)
      setPageNumber(1)
      setHasMore(true)
    } else {
      setLoadingMorePosts(true)
    }

    try {
      const currentPage = isInitialLoad ? 1 : pageNumber
      const response = await apiAdapterCoffeeWeb.getPostsWithPagination({ pageNumber: currentPage, pageSize, userId })

      const responseData = response?.data?.items || []
      const organizationStatus = response?.data?.organization_status

      setMediaList(response?.data)
      setOrganizationStatus(organizationStatus)
      onOrganizationStatusChange(organizationStatus)
      if (isInitialLoad) {
        setPosts(responseData)
      } else {
        setPosts((prev) => [...prev, ...responseData])
      }

      if (responseData.length < pageSize) {
        setHasMore(false)
      } else {
        setPageNumber((prev) => prev + 1)
      }
    } catch (error) {
      setHasMore(false)
    } finally {
      setLoadingPosts(false)
      setLoadingMorePosts(false)
    }
  }

  const loadMorePosts = () => {
    if (!loadingMorePosts && hasMore) {
      fetchAllMediaPosts(false)
    }
  }

  const itemTemplate = (item) => <>{item?.mediatype?.startsWith('video') ? <video controls src={item?.mediaurl} alt={item?.medianame} controlsList="nodownload noplaybackrate" disablePictureInPicture disableRemotePlayback /> : <img src={item?.mediaurl} alt={item?.medianame} />}</>

  const thumbnailTemplate = (item) => <img src={item?.mediaurl} alt={item?.medianame} />

  const closePostModal = () => {
    setShowModal(false)
  }

  const filteredPosts = posts?.filter((post) => post?.channelName?.toLowerCase().includes(searchTextField.toLowerCase()))

  const getTruncatedText = (text, maxLength) => {
    if (text.length <= maxLength) return text

    return `${text.substring(0, maxLength).trim()}`
  }

  const togglePostExpansion = (postId) => {
    setExpandedPosts((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(postId)) {
        newSet.delete(postId)
      } else {
        newSet.add(postId)
      }

      return newSet
    })
  }

  const handleCreatePost = (post) => {
    setPosts((prev) => [post, ...prev])
    setShowModal(false)
  }

  const getVisibleIndicators = (total, active) => {
    const maxVisible = 5

    if (total <= maxVisible) {
      return [...Array(total).keys()]
    }

    let start = Math.max(0, active - 2)
    let end = start + maxVisible

    if (end > total) {
      end = total
      start = end - maxVisible
    }

    return [...Array(end - start).keys()].map((i) => start + i)
  }

  const renderPostText = (post) => {
    const postId = post?._id || post?.channelName
    const description = post?.description?.trim() || ''
    const hasDescription = description?.length > 0
    const hasLink = Boolean(post?.channelLink)
    const isExpanded = expandedPosts.has(postId)
    const isLongText = description.length > MAX_DESCRIPTION_LENGTH

    if (!hasDescription && hasLink) {
      return (
        <div className="post-text-container">
          <p className="post-text">
            <span className="link-title">{t('LINK')}:</span>
            <a href={post.channelLink} target="_blank" rel="noreferrer" className="post-link-inline">
              {post?.channelLink}
            </a>
          </p>
        </div>
      )
    }

    let displayText = description

    if (!isExpanded && isLongText) {
      displayText = getTruncatedText(description, MAX_DESCRIPTION_LENGTH)
    }

    return (
      <div className="post-text-container">
        <p className="post-text">
          {displayText}

          {!isExpanded && (isLongText || hasLink) && (
            <>
              <span className="read-more-dots">...</span>
              <button className="read-more-btn" onClick={() => togglePostExpansion(postId)}>
                {t('READ_MORE') || 'Read More'}
              </button>
            </>
          )}

          {isExpanded && (
            <>
              {hasLink && (
                <>
                  <br />
                  <span className="link-title">{t('LINK')}:</span>
                  <a href={post.channelLink} target="_blank" rel="noreferrer" className="post-link-inline">
                    {post.channelLink}
                  </a>
                </>
              )}

              <>
                <span className="read-more-dots">...</span>
                <button className="read-less-btn" onClick={() => togglePostExpansion(postId)}>
                  {t('READ_LESS') || 'Read Less'}
                </button>
              </>
            </>
          )}
        </p>
      </div>
    )
  }

  const PostSkeleton = () => (
    <div className="post-skeleton-card">
      {/* Header */}
      <div className="post-skeleton-header">
        <Skeleton shape="circle" size="60px" className="post-skeleton-avatar" />
        <div className="post-skeleton-meta">
          <Skeleton width="150px" height="28px" className="post-skeleton-author" />
        </div>
      </div>

      {/* Media */}
      <div className="post-skeleton-media">
        <Skeleton width="100%" height="55vh" className="post-skeleton-image" />
      </div>

      {/* Description skeleton */}
      <div className="post-skeleton-text">
        <Skeleton width="100%" height="28px" className="post-skeleton-line" />
      </div>
    </div>
  )

  const renderFeedContent = () => {
    if (loadingPosts) {
      return Array?.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)
    }

    if (posts.length === 0 && !loadingPosts) {
      return (
        <div className="no-data-wrapper">
          <img src={NoData} alt="no-data" />
        </div>
      )
    }

    const handleSlideChange = (postId, index) => {
      setActiveIndexes((prev) => ({
        ...prev,
        [postId]: index
      }))
    }

    return (
      <>
        <InfiniteScroll
          dataLength={posts.length}
          next={loadMorePosts}
          hasMore={hasMore}
          scrollableTarget="feed-list-container"
          loader={
            loadingMorePosts && (
              <div>
                {Array?.from({ length: 2 }).map((_, i) => (
                  <PostSkeleton key={`loading-${i}`} />
                ))}
              </div>
            )
          }
          endMessage={
            !hasMore &&
            posts.length > 0 &&
            filteredPosts.length > 0 && (
              <div className="end-of-posts-message">
                <p>{t('NO_MORE_POSTS') || 'No more posts to load'}</p>
              </div>
            )
          }
        >
          {filteredPosts?.map((post, index) => {
            const currentIndex = activeIndexes[post._id] || 0

            return (
              <div className="post-card" key={post?._id || index}>
                <div className="post-header">
                  <div className="author-info">
                    <img src={post?.channelLogo} alt="avatar-logo" />
                  </div>

                  <div className="post-meta">
                    <h4 className="author-name">{post?.channelName}</h4>
                    <span className="author-country">{post?.country}</span>
                  </div>
                </div>

                {post?.media?.length > 0 && (
                  <div className="post-image-wrapper">
                    <Galleria value={post.media} activeIndex={activeIndexes[post._id] || 0} onItemChange={(e) => handleSlideChange(post._id, e.index)} circular showItemNavigators={post.media.length > 1} showIndicators={false} showThumbnails={false} item={itemTemplate} />
                    {post?.media?.length > 1 && (
                      <div className="slider-indicators">
                        {getVisibleIndicators(post.media.length, currentIndex).map((index) => {
                          const distance = Math.abs(index - currentIndex)

                          let dotClass = 'dot-far'

                          if (distance === 0) {
                            dotClass = 'dot-active'
                          } else if (distance === 1) {
                            dotClass = 'dot-near'
                          }

                          return <span key={index} className={`carousel-dot ${dotClass}`} />
                        })}
                      </div>
                    )}
                  </div>
                )}
                {renderPostText(post)}
              </div>
            )
          })}
        </InfiniteScroll>

        {!loadingPosts && filteredPosts?.length === 0 && posts.length > 0 && (
          <div className="no-data-wrapper">
            <img src={NoData} alt="no-data" />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div className="coffee-feed-wrapper">
        <div className="coffee-feed-container">
          <div ref={containerRef} id="feed-list-container" className={`feed-list ${!userDetails ? 'guest-feed-list' : ''} ${hasHeader ? 'with-header' : ''}`}>
            {renderFeedContent()}
          </div>
          <div className={`coffee-communities-wrapper ${!userDetails ? 'guest-list' : ''} ${hasHeader ? 'with-header' : ''}`}>
            <div className="channel-tabs-wrapper">
              <div
                className={`channel-tab ${activeTab === 'communityChat' ? 'channel-tab--active' : ''}`}
                onClick={() => {
                  setActiveTab('communityChat')
                  setCameFromCommunityChat(false) // Reset when manually switching tabs
                  const params = new URLSearchParams(location.search)

                  params.set('chatTab', 'communityChat')
                  params.delete('userId')
                  history.replace({ pathname: location.pathname, search: params.toString() })
                }}
              >
                {t('COMMUNITY_CHATS')}
                {communityUnreadCount > 0 && <span className="chat-tab-badge">{communityUnreadCount > 99 ? '99+' : communityUnreadCount}</span>}
              </div>
              {/* {isPersonalChatAvailable && userDetails && ( */}
              <div
                className={`channel-tab ${activeTab === 'personalChat' ? 'channel-tab--active' : ''}`}
                onClick={() => {
                  setActiveTab('personalChat')
                  setCameFromCommunityChat(false) // Reset when manually switching tabs
                  const params = new URLSearchParams(location.search)

                  params.set('chatTab', 'personalChat')
                  history.replace({ pathname: location.pathname, search: params.toString() })
                }}
              >
                {t('PERSONAL_CHATS')}
                {personalUnreadCount > 0 && <span className="chat-tab-badge">{personalUnreadCount > 99 ? '99+' : personalUnreadCount}</span>}
              </div>
              {/* )} */}
            </div>

            {activeTab === 'communityChat' && (
              <CoffeeCommunities
                isCoffeePost={true}
                selectedCommunityId={selectedCommunityId}
                onCommunitySelect={setSelectedCommunityId}
                onBackToCommunities={() => setSelectedCommunityId(null)}
                hasHeader={hasHeader}
                onUnreadCountRefresh={fetchUnreadCounts}
                onSwitchToPersonalChat={(userId) => {
                  setActiveTab('personalChat')
                  setSelectedUserId(userId)
                  setCameFromCommunityChat(true) // Track that we came from community chat
                  // Update URL params
                  const params = new URLSearchParams(location.search)

                  params.set('chatTab', 'personalChat')
                  if (userId) {
                    params.set('userId', userId)
                  }
                  history.replace({ pathname: location.pathname, search: params.toString() })
                }}
              />
            )}

            {activeTab === 'personalChat' && (
              <PersonalChat
                isCoffeePost={true}
                selectedUserId={selectedUserId}
                onUnreadCountRefresh={fetchUnreadCounts}
                onUserSelect={(userId) => {
                  setSelectedUserId(userId)
                  // Update URL params
                  const params = new URLSearchParams(location.search)

                  params.set('chatTab', 'personalChat')
                  if (userId) {
                    params.set('userId', userId)
                  } else {
                    params.delete('userId')
                  }
                  history.replace({ pathname: location.pathname, search: params.toString() })
                }}
                onBackToPersonalChats={() => {
                  setSelectedUserId(null)

                  // If we came from community chat, go back to it
                  if (cameFromCommunityChat) {
                    setActiveTab('communityChat')
                    setCameFromCommunityChat(false)
                    // Update URL params
                    const params = new URLSearchParams(location.search)

                    params.set('chatTab', 'communityChat')
                    params.delete('userId')
                    history.replace({ pathname: location.pathname, search: params.toString() })
                  } else {
                    // Otherwise, just go back to personal chat list
                    // Update URL params
                    const params = new URLSearchParams(location.search)

                    params.set('chatTab', 'personalChat')
                    params.delete('userId')
                    history.replace({ pathname: location.pathname, search: params.toString() })
                  }
                }}
                hasHeader={hasHeader}
              />
            )}
          </div>
        </div>
      </div>
      <CreatePostModal visible={showModal} closeModal={closePostModal} organizationStatus={organizationStatus} mediaList={mediaList} onOrganizationStatusChange={onOrganizationStatusChange} onPost={handleCreatePost} />
    </>
  )
}

export default CoffeePostScreen
